import React, { useState, useEffect, useRef } from 'react';
import {
  X, Loader2, Bot, ArrowRight, Check,
  ChevronRight, Sparkles, Lock, AlertCircle,
  ThumbsUp, MessageSquare, Send
} from 'lucide-react';
import { runCampaignWizard, runCampaignPlan } from '../lib/campaignPipeline';

// ─── Typing Animation Helper ──────────────────────────────────────────────────

function TypingDots() {
  return (
    <span className="inline-flex items-center gap-1 ml-1">
      {[0, 1, 2].map(i => (
        <span
          key={i}
          className="size-1.5 rounded-full bg-[#02B6FF] animate-bounce"
          style={{ animationDelay: `${i * 150}ms`, animationDuration: '0.8s' }}
        />
      ))}
    </span>
  );
}

// ─── Chat Bubble Component ────────────────────────────────────────────────────

function AgentBubble({ children, animate }) {
  const [show, setShow] = useState(!animate);
  const [showTyping, setShowTyping] = useState(animate);

  useEffect(() => {
    if (!animate) return;
    const t = setTimeout(() => {
      setShowTyping(false);
      setShow(true);
    }, 900);
    return () => clearTimeout(t);
  }, [animate]);

  return (
    <div className="flex items-start gap-3">
      <div className="size-7 rounded-full bg-[#02B6FF]/15 border border-[#02B6FF]/30 flex items-center justify-center shrink-0 mt-0.5">
        <Bot size={13} className="text-[#33D5E6]" />
      </div>
      <div className="bg-[#1a1e2a] border border-white/[0.08] rounded-2xl rounded-tl-sm px-4 py-3 max-w-lg">
        {showTyping && !show ? (
          <div className="flex items-center gap-2 py-1 font-mono">
            <span className="text-[10px] text-slate-400">Builder is thinking</span>
            <TypingDots />
          </div>
        ) : show ? (
          <div className="text-xs text-slate-200 leading-relaxed font-sans">{children}</div>
        ) : null}
      </div>
    </div>
  );
}

// ─── Markdown Parser Helpers ──────────────────────────────────────────────────

function renderMarkdown(md) {
  if (!md) return null;
  const lines = md.split('\n');
  return lines.map((line, i) => {
    const trimmed = line.trim();
    if (trimmed.startsWith('# ')) {
      return <h1 key={i} className="text-xs font-black text-white mt-4 mb-2 border-b border-white/10 pb-1 font-mono uppercase tracking-wider">{trimmed.slice(2)}</h1>;
    }
    if (trimmed.startsWith('## ')) {
      return <h2 key={i} className="text-[11px] font-black text-[#02B6FF] mt-3 mb-1.5 font-mono uppercase tracking-wider">{trimmed.slice(3)}</h2>;
    }
    if (trimmed.startsWith('### ')) {
      return <h3 key={i} className="text-[10px] font-bold text-[#33D5E6] mt-2 mb-1 font-mono uppercase tracking-wider">{trimmed.slice(4)}</h3>;
    }
    if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      const content = parseBoldText(trimmed.slice(2));
      return (
        <li key={i} className="list-disc list-inside text-[10px] text-slate-300 ml-2 mb-1 font-sans leading-relaxed">
          {content}
        </li>
      );
    }
    if (trimmed === '') {
      return <div key={i} className="h-2" />;
    }
    const content = parseBoldText(trimmed);
    return <p key={i} className="text-[10px] text-slate-300 leading-relaxed mb-1.5 font-sans">{content}</p>;
  });
}

function parseBoldText(text) {
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="text-white font-bold">{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

// ─── Main Modal Drawer ─────────────────────────────────────────────────────────

export default function PageBuilderAgentModal({
  isOpen,
  onClose,
  campaign,          // full campaign object
  angle,             // angle plan record { id, name, funnelType, notes, ... }
  coachResult,       // coachAnalysis from the campaign
  onPageBuilt,       // callback(html) when page is ready
}) {
  const [building, setBuilding] = useState(false);
  const [buildError, setBuildError] = useState(null);
  const [done, setDone] = useState(false);
  const bottomRef = useRef(null);

  // Layout Planning & Chat Revision States
  const [plan, setPlan] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [regeneratingPlan, setRegeneratingPlan] = useState(false);
  const [planStatus, setPlanStatus] = useState('');

  const getEnrichedPayload = (currentFeedback = null) => {
    const isCollectEmails = campaign?.collectEmails !== false && campaign?.collectEmails !== 'false';
    const angleFunnel = angle?.funnelType || angle?.data?.funnelType || '';
    const isDirectPresell = !isCollectEmails || (!angleFunnel.toLowerCase().includes('optin') && !angleFunnel.toLowerCase().includes('squeeze') && !angleFunnel.toLowerCase().includes('lead magnet'));

    const campaignName = campaign?.name || '';
    const offerDescription = campaign?.offer_description || campaign?.niche || '';
    const targetGeo = campaign?.targetGeo || '';
    const angleName = angle?.name || angle?.data?.angleName || '';
    const angleNotes = angle?.notes || angle?.data?.angleNotes || angle?.data?.notes || '';

    const enrichedCoach = {
      ...(coachResult || {}),
      collect_emails: !isDirectPresell,
      is_direct_presell: isDirectPresell,
      campaign_name: campaignName,
      offer_name: campaignName,
      offer_description: offerDescription,
      target_geo: targetGeo,
      offer_summary: {
        name: campaignName || 'Affiliate Offer',
        brand: campaignName || 'Official Sponsor',
        description: offerDescription || campaignName || '',
        target_geo: targetGeo || 'All',
        payout: campaign?.payout || '',
        network: campaign?.network || '',
        ...(coachResult?.offer_summary || {})
      },
      audience: {
        profile: coachResult?.audience?.profile || (targetGeo ? `Residents in ${targetGeo} interested in ${campaignName}` : `Target audience for ${campaignName || 'this offer'}`),
        core_desire: coachResult?.audience?.core_desire || (angleNotes || `Claiming or winning ${campaignName || 'the offer'}`),
        emotional_state: coachResult?.audience?.emotional_state || 'Hopeful, curious, and motivated',
        ...(coachResult?.audience || {})
      },
      page_builder_context: {
        campaign_name: campaignName,
        offer_name: campaignName,
        offer_description: offerDescription,
        target_geo: targetGeo,
        target_audience: coachResult?.audience?.profile || (targetGeo ? `Residents in ${targetGeo} interested in ${campaignName}` : `Consumers interested in ${campaignName || 'this offer'}`),
        pain_point: coachResult?.audience?.core_desire || angleNotes || `Taking advantage of ${campaignName}`,
        tone: coachResult?.design_brief?.brand_tone || 'curiosity',
        hooks: coachResult?.copywriting?.hooks?.[0] || angleNotes || '',
        angle_name: angleName,
        funnel_type: angleFunnel || (isDirectPresell ? 'Direct Pre-Sell' : 'Optin / Squeeze Page'),
        angle_notes: angleNotes,
        collect_emails: !isDirectPresell,
        is_direct_presell: isDirectPresell,
        funnel_directive: isDirectPresell 
          ? `Direct Pre-Sell Only: DO NOT generate any email capture form or input elements. The entire page copy and hook must be built directly around '${campaignName}' (${offerDescription}). All CTA buttons must link directly to the affiliate tracking link.` 
          : `Lead Capture: Include an email opt-in capture form to enter for '${campaignName}'.`
      },
      selected_angle: { 
        name: angleName, 
        funnel: angleFunnel || (isDirectPresell ? 'Direct Pre-Sell' : 'Optin / Squeeze Page')
      },
    };

    return {
      campaignName: campaignName,
      offerName: campaignName,
      affiliateLink: campaign?.affiliateLink || '',
      network: campaign?.network || '',
      payout: campaign?.payout || '',
      payoutModel: campaign?.payoutModel || '',
      targetGeo: targetGeo,
      networkEpc: campaign?.networkEpc || '',
      offer_preview_url: campaign?.offer_preview_url || '',
      offer_description: offerDescription,
      trafficSource: campaign?.trafficSource || null,
      collect_emails: !isDirectPresell,
      is_direct_presell: isDirectPresell,
      campaign_json: enrichedCoach,
      page_builder_context: enrichedCoach.page_builder_context,
      ...(currentFeedback ? {
        previous_plan: plan,
        feedback: currentFeedback
      } : {})
    };
  };

  const handleGeneratePlan = async () => {
    setGeneratingPlan(true);
    setBuildError(null);
    setPlanStatus('Initializing planning engine...');

    try {
      const apiKey = localStorage.getItem('ninja_api_key') || '';
      const payload = getEnrichedPayload();
      const planMarkdown = await runCampaignPlan(payload, apiKey, {
        onStatus: (msg) => setPlanStatus(msg),
      });
      setPlan(planMarkdown);
    } catch (err) {
      console.error('[PageBuilder] Plan generation failed:', err);
      setBuildError(err.message || 'Plan generation failed. Please try again.');
    } finally {
      setGeneratingPlan(false);
    }
  };

  // Reset and auto-trigger plan generation on open
  useEffect(() => {
    if (!isOpen) return;
    setBuilding(false);
    setBuildError(null);
    setDone(false);
    setPlan(null);
    setFeedback('');
    setGeneratingPlan(true);
    setRegeneratingPlan(false);
    setPlanStatus('Initializing planning engine...');

    const t = setTimeout(() => {
      handleGeneratePlan();
    }, 600);
    return () => clearTimeout(t);
  }, [isOpen, angle]);

  // Auto-scroll to bottom as chat updates
  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [building, done, plan, generatingPlan, regeneratingPlan]);

  if (!isOpen) return null;

  const handleRegeneratePlan = async () => {
    if (!feedback.trim()) return;
    setRegeneratingPlan(true);
    setBuildError(null);
    setPlanStatus('Revising layout plan based on your feedback...');

    try {
      const apiKey = localStorage.getItem('ninja_api_key') || '';
      const payload = getEnrichedPayload(feedback);
      const revisedPlan = await runCampaignPlan(payload, apiKey, {
        onStatus: (msg) => setPlanStatus(msg),
      });
      setPlan(revisedPlan);
      setFeedback('');
    } catch (err) {
      console.error('[PageBuilder] Plan revision failed:', err);
      setBuildError(err.message || 'Failed to revise plan. Please try again.');
    } finally {
      setRegeneratingPlan(false);
    }
  };

  const handleBuildPage = async () => {
    setBuilding(true);
    setBuildError(null);

    try {
      const apiKey = localStorage.getItem('ninja_api_key') || '';
      const payload = {
        ...getEnrichedPayload(),
        page_plan: plan
      };

      const result = await runCampaignWizard(payload, apiKey, {
        onStatus: (msg) => console.log('[PageBuilder]', msg),
      });

      setDone(true);
      if (onPageBuilt) onPageBuilt(result.html, result.coachAnalysis);
    } catch (err) {
      console.error('[PageBuilder] Page build failed:', err);
      setBuildError(err.message || 'Page building failed. Please try again.');
      setBuilding(false);
    }
  };

  const angleName = angle?.name || angle?.data?.angleName || 'Angle';
  const funnelType = angle?.funnelType || angle?.data?.funnelType || '';

  return (
    <>
      {/* Backdrop overlay */}
      <div 
        onClick={onClose} 
        className="fixed inset-0 bg-black/60 z-[290] backdrop-blur-sm transition-opacity" 
      />

      {/* Side Panel Drawer */}
      <div className="fixed inset-y-0 right-0 w-full max-w-md sm:max-w-lg bg-[#13161d] border-l border-white/[0.08] shadow-2xl z-[300] flex flex-col h-full transform transition-transform duration-300 animate-in slide-in-from-right font-mono">

        {/* Header */}
        <div className="px-6 py-4 border-b border-white/[0.08] flex items-center justify-between shrink-0 font-mono">
          <div className="flex items-center gap-3">
            <div className="size-8 rounded-full bg-[#02B6FF]/15 border border-[#02B6FF]/30 flex items-center justify-center">
              <Bot size={15} className="text-[#33D5E6]" />
            </div>
            <div>
              <div className="text-xs font-bold text-white uppercase tracking-wider">Page Builder Agent</div>
              <div className="text-[9px] text-slate-500">
                Building: <span className="text-[#02B6FF] font-bold">{angleName}</span>
                {funnelType && (
                  <span className="ml-2 text-slate-600">· {funnelType}</span>
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-1 text-slate-500 hover:text-white hover:bg-white/5 rounded transition-colors cursor-pointer">
            <X size={16} />
          </button>
        </div>

        {/* Status Header */}
        <div className="px-6 py-2 bg-[#0d0f14]/50 border-b border-white/[0.04] flex items-center justify-between shrink-0 text-[9px] text-slate-500">
          <span>STATUS: {done ? 'COMPLETE' : plan ? 'PLANNING REVIEW' : 'DRAFTING PLAN'}</span>
          <span className="text-[#33D5E6] font-bold">AUTONOMOUS AGENT ACTIVE</span>
        </div>

        {/* Chat / Plan Review Area */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5 custom-scrollbar">

          {/* Intro Message */}
          <AgentBubble animate={false}>
            <span className="font-bold text-white">Hello! I'm your Page Builder Agent.</span>
            <br />
            I'm analyzing your campaign strategy to draft a detailed **landing page plan** for <strong className="text-[#02B6FF]">{angleName}</strong>.
          </AgentBubble>

          {/* Planning Loader */}
          {generatingPlan && (
            <div className="space-y-4">
              <div className="ml-10 flex items-center gap-3 bg-[#0d0f14] border border-white/[0.06] rounded-xl px-4 py-3">
                <Loader2 size={14} className="animate-spin text-[#02B6FF] shrink-0" />
                <span className="text-[10px] text-slate-400 font-sans">
                  {planStatus || 'Drafting page plan...'}
                </span>
              </div>
            </div>
          )}

          {/* Plan Display and Revision Chat */}
          {plan && !generatingPlan && !building && !done && (
            <div className="space-y-4 animate-in fade-in duration-200">
              <AgentBubble animate={false}>
                <span className="font-bold text-white">I have generated your Page Plan!</span>
                <br />
                Please review the conversion layout and copywriting strategy below.
              </AgentBubble>

              {/* Scrollable Plan Box */}
              <div className="ml-10 bg-[#0a0c10] border border-white/[0.06] rounded-2xl p-5 space-y-3 max-h-[380px] overflow-y-auto custom-scrollbar shadow-inner text-left">
                <div className="text-[10px] font-bold text-[#02B6FF] uppercase tracking-widest border-b border-white/5 pb-2 font-mono flex items-center gap-1.5">
                  <Check size={11} className="text-[#33D5E6]" /> Proposed Landing Page Plan
                </div>
                <div className="prose prose-invert max-w-none text-left">
                  {renderMarkdown(plan)}
                </div>
              </div>

              {/* Instructions Bubble */}
              <AgentBubble animate={true}>
                <div className="space-y-2">
                  <p className="font-bold text-[#33D5E6] flex items-center gap-1.5 font-mono uppercase tracking-wider text-[10px]">
                    <Sparkles size={11} /> Next Steps:
                  </p>
                  <p className="text-slate-300 font-sans text-[11px] leading-relaxed">
                    👉 Click <strong className="text-white">Accept & Build Page</strong> to compile the final page HTML code using Claude API & Google Imagen.<br />
                    💬 Or type in the chat below to request revisions to the plan.
                  </p>
                </div>
              </AgentBubble>

              {/* Revision Prompt Input */}
              {regeneratingPlan ? (
                <div className="ml-10 flex items-center gap-3 bg-[#0d0f14] border border-white/[0.06] rounded-xl px-4 py-3">
                  <Loader2 size={14} className="animate-spin text-[#02B6FF] shrink-0" />
                  <span className="text-[10px] text-slate-400 font-sans">
                    {planStatus || 'Revising page plan...'}
                  </span>
                </div>
              ) : (
                <div className="ml-10 space-y-3 bg-[#1a1d27]/40 border border-white/[0.04] rounded-2xl p-4">
                  <div className="space-y-1">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                      <MessageSquare size={11} className="text-[#33D5E6]" /> Request Layout Revisions
                    </span>
                  </div>
                  
                  <textarea
                    value={feedback}
                    onChange={e => setFeedback(e.target.value)}
                    placeholder="e.g. Make the headline more emotional, add a detailed FAQ section at the end..."
                    rows={2}
                    className="w-full bg-[#0d0f14] border border-white/[0.08] focus:border-[#02B6FF] rounded-xl px-3 py-2.5 text-xs text-white font-sans focus:outline-none resize-none transition-colors"
                  />

                  <div className="flex justify-between gap-3">
                    <button
                      type="button"
                      onClick={handleRegeneratePlan}
                      disabled={!feedback.trim()}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-xl font-bold text-[9px] uppercase tracking-wider transition-all border ${
                        feedback.trim()
                          ? 'bg-slate-800 border-white/10 hover:bg-slate-700 text-white cursor-pointer'
                          : 'bg-white/5 border-transparent text-slate-600 cursor-not-allowed'
                      }`}
                    >
                      <RotateIcon size={10} /> Revise Plan
                    </button>

                    <button
                      type="button"
                      onClick={handleBuildPage}
                      className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-[#33D5E6] via-[#02B6FF] to-[#0A22EE] hover:from-[#33D5E6] hover:to-[#02B6FF] text-white font-bold text-[9px] uppercase tracking-wider rounded-xl transition-all cursor-pointer shadow-md shadow-[#02B6FF]/20"
                    >
                      <ThumbsUp size={10} /> Accept & Build Page
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Building state */}
          {building && (
            <div className="space-y-4">
              <AgentBubble animate={false}>
                <span className="font-bold text-white">Perfect! Plan approved.</span>
                <br />
                Compiling and building your landing page HTML using **Claude API** for copy & **Google Imagen 3 API** for visuals…
              </AgentBubble>
              <div className="ml-10 flex items-center gap-3 bg-[#0d0f14] border border-white/[0.06] rounded-xl px-4 py-3">
                <Loader2 size={14} className="animate-spin text-[#02B6FF] shrink-0" />
                <span className="text-[10px] text-slate-400 font-sans">
                  Generating custom page layout & graphics...
                </span>
              </div>
            </div>
          )}

          {/* Error */}
          {buildError && (
            <div className="ml-10 bg-red-500/10 border border-red-500/20 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle size={14} className="text-red-400 shrink-0 mt-0.5" />
              <div className="space-y-2">
                <p className="text-xs text-red-400 font-sans">{buildError}</p>
                <button
                  type="button"
                  onClick={plan ? handleBuildPage : handleGeneratePlan}
                  className="text-[10px] font-bold text-white bg-red-500/20 border border-red-500/30 px-3 py-1.5 rounded-lg hover:bg-red-500/30 transition-colors cursor-pointer"
                >
                  Try Again
                </button>
              </div>
            </div>
          )}

          {/* Done / Complete */}
          {done && (
            <div className="space-y-3">
              <AgentBubble animate={false}>
                <div className="flex items-center gap-2">
                  <span className="text-base">🎉</span>
                  <span className="font-bold text-white font-mono uppercase tracking-wider">Page Ready!</span>
                </div>
                <p className="text-[11px] text-slate-300 mt-1 font-sans">
                  The <strong className="text-[#33D5E6]">{angleName}</strong> page has been built and saved to your campaign. You can preview and edit it from the Funnel Map.
                </p>
              </AgentBubble>
              <div className="ml-10 font-mono">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex items-center gap-2 px-5 py-2.5 bg-[#22c55e] hover:bg-[#16a34a] text-white font-bold text-[10px] uppercase tracking-wider rounded-xl transition-colors cursor-pointer shadow-md shadow-[#22c55e]/20"
                >
                  <Check size={12} /> View in Funnel Map
                </button>
              </div>
            </div>
          )}

          <div ref={bottomRef} />
        </div>
      </div>
    </>
  );
}

// Icon helper for rotation
function RotateIcon({ size }) {
  return (
    <svg className="animate-spin-slow" style={{ width: size, height: size }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 7.89M9 11l3 3L22 4" />
    </svg>
  );
}
